import { DynamicFiltersInput } from '../../../../apis/graphql/reports/standardDeliveryDHC/types';
import { getCampaignIds } from '../../../../apis/graphql/reports/utils/utils';
import {
  StandardDeliveryDeviceHigherCategoryReportFiltersInput,
  StandardDeliveryDeviceHigherCategoryReportMetrics,
} from '../../../../apis/graphql/types';
import { CellStatus, CellWithMergedRows } from '../../../../components/BlueTheme/Table/types';
import { StorageCampaign } from '../../types/reports';
import { StandardDeliveryDHCReportTableData } from './types';

type MergedCellsCount = {
  firstLevelRowsCount: number;
  secondLevelRowsCount: number;
};

const UNKNOWN = 'Unknown';

export const flattenMetrics = (
  reports: StandardDeliveryDeviceHigherCategoryReportMetrics[]
): StandardDeliveryDHCReportTableData[] => {
  return reports.reduce((result: StandardDeliveryDHCReportTableData[], report) => {
    const firstLevelRowsCount = report.lineItems.reduce((accumulator, currentValue) => {
      accumulator = accumulator + (currentValue.deviceBreakout?.length ?? 0);
      return accumulator;
    }, 0);

    report.lineItems.forEach((lineItem, lineItemIndex) => {
      const secondLevelRowsCount = lineItem.deviceBreakout?.length ?? 0;

      if (lineItem.deviceBreakout?.length) {
        lineItem.deviceBreakout.forEach(({ completions, deviceBreakdown, deviceName, impressions }, deviceIndex) => {
          const cellMerges = getCellsWithMergedRows(
            { firstLevelRowsCount, secondLevelRowsCount },
            lineItemIndex === 0 && deviceIndex === 0 ? CellStatus.main : CellStatus.merged,
            deviceIndex === 0 ? CellStatus.main : CellStatus.merged
          );

          result.push({
            lineItemName: report.lineItemName,
            campaignName: report.campaignName ?? '',
            accountManager: report.accountManager ?? UNKNOWN,
            accountExecutive: report.accountExecutive ?? UNKNOWN,
            adProduct: lineItem.adProduct,
            publisher: lineItem.publisher,
            cpm: lineItem.cpm,
            deliveryGoal: lineItem.deliveryGoal,
            completions,
            deviceBreakdown,
            deviceName,
            impressions,
            endDate: lineItem.endDate,
            id: report.id,
            lifetimeCompletions: lineItem.lifetimeCompletions,
            revenue: lineItem.revenue,
            startDate: lineItem.startDate,
            cellMerges,
          });
        });
      } else {
        const cellMerges = getCellsWithMergedRows(
          { firstLevelRowsCount, secondLevelRowsCount },
          lineItemIndex === 0 ? CellStatus.main : CellStatus.merged,
          lineItemIndex === 0 ? CellStatus.main : CellStatus.merged
        );

        result.push({
          lineItemName: report.lineItemName,
          campaignName: report.campaignName ?? '',
          accountManager: report.accountManager ?? UNKNOWN,
          accountExecutive: report.accountExecutive ?? UNKNOWN,
          adProduct: lineItem.adProduct,
          publisher: lineItem.publisher,
          cpm: lineItem.cpm,
          deliveryGoal: lineItem.deliveryGoal,
          endDate: lineItem.endDate,
          id: report.id,
          lifetimeCompletions: lineItem.lifetimeCompletions,
          revenue: lineItem.revenue,
          startDate: lineItem.startDate,
          cellMerges,
        });
      }
    });
    return result;
  }, []);
};

// exported for testing purpose only
export const getCellsWithMergedRows = (
  { firstLevelRowsCount, secondLevelRowsCount }: MergedCellsCount,
  firstLevelStatus: CellStatus,
  secondLevelStatus: CellStatus
): CellWithMergedRows[] => {
  return [
    {
      cellId: 'campaignName',
      mergedCellsCount: firstLevelRowsCount,
      status: firstLevelStatus,
    },
    {
      cellId: 'lineItemName',
      mergedCellsCount: firstLevelRowsCount,
      status: firstLevelStatus,
    },
    {
      cellId: 'adProduct',
      mergedCellsCount: secondLevelRowsCount,
      status: secondLevelStatus,
    },
    {
      cellId: 'publisher',
      mergedCellsCount: secondLevelRowsCount,
      status: secondLevelStatus,
    },
    {
      cellId: 'lineItemStartDate',
      mergedCellsCount: secondLevelRowsCount,
      status: secondLevelStatus,
    },
    {
      cellId: 'lineItemEndDate',
      mergedCellsCount: secondLevelRowsCount,
      status: secondLevelStatus,
    },
    {
      cellId: 'cpm',
      mergedCellsCount: secondLevelRowsCount,
      status: secondLevelStatus,
    },
    {
      cellId: 'revenue',
      mergedCellsCount: secondLevelRowsCount,
      status: secondLevelStatus,
    },
    {
      cellId: 'deliveryGoal',
      mergedCellsCount: secondLevelRowsCount,
      status: secondLevelStatus,
    },
    {
      cellId: 'accountExecutive',
      mergedCellsCount: secondLevelRowsCount,
      status: secondLevelStatus,
    },
    {
      cellId: 'accountManager',
      mergedCellsCount: secondLevelRowsCount,
      status: secondLevelStatus,
    },
    {
      cellId: 'lifetimeCompletions',
      mergedCellsCount: secondLevelRowsCount,
      status: secondLevelStatus,
    },
  ];
};

export const getStandardDeliveryDHCReportFilters = (
  organizationId: string,
  advertiserId: string,
  dynamicFilters: DynamicFiltersInput,
  campaigns?: StorageCampaign[]
): StandardDeliveryDeviceHigherCategoryReportFiltersInput => {
  return {
    advertiserId,
    organizationId,
    campaignIds: getCampaignIds(campaigns),
    cpm: dynamicFilters.cpm || null,
    revenue: dynamicFilters.revenue || null,
    startDate: dynamicFilters.startDate || null,
    endDate: dynamicFilters.endDate || null,
    adProduct: dynamicFilters.adProduct || null,
    deliveryGoal: dynamicFilters.deliveryGoal || null,
    lifetimeCompletions: dynamicFilters.lifetimeCompletions || null,
  };
};
